import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { 
  DollarSign, Download, Calendar, TrendingUp, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function PayrollPage() {
  const { api, user } = useAuth();
  const [currentPayroll, setCurrentPayroll] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [currentRes, historyRes] = await Promise.all([
        api.get('/payroll/current'),
        api.get('/payroll/history')
      ]);
      setCurrentPayroll(currentRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to fetch payroll data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatWeekRange = (start, end) => {
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[#F59E0B]/20 text-[#F59E0B]',
      approved: 'bg-[#10B981]/20 text-[#10B981]',
      paid: 'bg-[#D4AF37]/20 text-[#D4AF37]'
    };
    return styles[status] || 'bg-white/10 text-white';
  };

  const handleExport = () => {
    toast.success('Payroll report exported');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="payroll-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payroll & <span className="text-[#D4AF37]">Earnings</span>
            </h1>
            <p className="text-white/50 mt-1">Automated payroll calculations and history</p>
          </div>
          <Button
            onClick={handleExport}
            variant="outline"
            className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
            data-testid="btn-export"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>

        {/* Current Week Card */}
        <Card className="glass-card border-0 border-l-4 border-l-[#D4AF37]">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-white">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              Current Pay Period
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-white/50">Loading...</div>
            ) : currentPayroll ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                <div className="col-span-2 md:col-span-1">
                  <p className="text-white/50 text-sm mb-1">Gross Pay</p>
                  <p className="text-4xl font-bold shimmer" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    ${currentPayroll.gross_pay.toFixed(2)}
                  </p>
                </div>
                
                <div>
                  <p className="text-white/50 text-sm mb-1">Total Hours</p>
                  <p className="text-2xl font-bold text-white font-mono">
                    {currentPayroll.total_hours.toFixed(1)}h
                  </p>
                </div>
                
                <div>
                  <p className="text-white/50 text-sm mb-1">Billable Hours</p>
                  <p className="text-2xl font-bold text-[#10B981] font-mono">
                    {currentPayroll.billable_hours.toFixed(1)}h
                  </p>
                </div>
                
                <div>
                  <p className="text-white/50 text-sm mb-1">Lunch Deducted</p>
                  <p className="text-2xl font-bold text-white/70 font-mono">
                    {currentPayroll.lunch_hours.toFixed(1)}h
                  </p>
                </div>
                
                <div>
                  <p className="text-white/50 text-sm mb-1">Rate</p>
                  <p className="text-2xl font-bold text-[#D4AF37] font-mono">
                    ${currentPayroll.contract_rate}/hr
                  </p>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-white/50">No payroll data available</div>
            )}
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">YTD Earnings</p>
                  <p className="text-xl font-bold text-white font-mono">
                    ${history.reduce((sum, p) => sum + (p.gross_pay || 0), 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">YTD Hours</p>
                  <p className="text-xl font-bold text-white font-mono">
                    {history.reduce((sum, p) => sum + (p.billable_hours || 0), 0).toFixed(0)}h
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#4B167A]/40 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Avg Weekly</p>
                  <p className="text-xl font-bold text-white font-mono">
                    ${history.length > 0 
                      ? (history.reduce((sum, p) => sum + (p.gross_pay || 0), 0) / history.length).toFixed(2)
                      : '0.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1F1B71]/60 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white/70" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Pay Periods</p>
                  <p className="text-xl font-bold text-white">{history.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payroll History */}
        <Card className="glass-card border-0">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-2 text-white">
              <DollarSign className="w-5 h-5 text-[#D4AF37]" />
              Payroll History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-white/50">Loading history...</div>
            ) : history.length === 0 ? (
              <div className="p-8 text-center text-white/50">No payroll history available</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Total Hours</th>
                      <th>Billable Hours</th>
                      <th>Rate</th>
                      <th>Gross Pay</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((record) => (
                      <tr key={record.id} data-testid={`payroll-row-${record.id}`}>
                        <td className="font-mono text-sm">
                          {formatWeekRange(record.week_start, record.week_end)}
                        </td>
                        <td className="font-mono">{record.total_hours.toFixed(1)}h</td>
                        <td className="font-mono text-[#10B981]">{record.billable_hours.toFixed(1)}h</td>
                        <td className="font-mono">${record.contract_rate}/hr</td>
                        <td className="font-mono font-bold text-[#D4AF37]">${record.gross_pay.toFixed(2)}</td>
                        <td>
                          <Badge className={getStatusBadge(record.status)}>
                            {record.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
